import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, output, signal } from '@angular/core';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { TranslatePipe } from '@ngx-translate/core';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatIcon } from '@angular/material/icon';
import { ImportElementType, ImportTreeNode } from '../../../models/classic-import.model';
import {
  FolderState,
  TreeFolder,
  TreeRow,
  buildFolderTree,
  closure,
  folderState,
  indexTree,
  requirers,
  toSelection,
  toggleElement,
  toggleKeys,
  treeRows,
} from '../classic-import-selection';

const TYPES: readonly ImportElementType[] = [
  'TEST_CASE',
  'TEST_MODULE',
  'DATA_SHEET',
  'OBJECT_LIBRARY',
  'TEST_PLAN',
  'REQUIREMENT',
  'DOCUMENT',
];
export const ICONS: Record<ImportElementType, string> = {
  TEST_CASE: 'fact_check',
  TEST_MODULE: 'extension',
  DATA_SHEET: 'table_chart',
  OBJECT_LIBRARY: 'inventory_2',
  TEST_PLAN: 'event_note',
  REQUIREMENT: 'rule',
  DOCUMENT: 'description',
};
/** Ab so vielen Elementen starten die Ordner zugeklappt. */
const COLLAPSE_ABOVE = 300;
const MAX_NAMES = 10;
const ROW_HEIGHT_PX = 36;

interface NeededBy {
  names: string[];
  more: number;
}

/**
 * Inhalt einer Classic-Datei als Baum zum Auswählen. Abhängigkeiten gewählter Elemente sind automatisch mitgewählt
 * und gesperrt. Die Zeilen liegen flach in einem virtuellen Viewport, damit auch große Dateien flüssig bleiben.
 */
@Component({
  selector: 'app-classic-import-tree',
  imports: [
    ScrollingModule,
    TranslatePipe,
    MatCheckbox,
    MatFormField,
    MatLabel,
    MatInput,
    MatSelect,
    MatOption,
    MatIcon,
  ],
  templateUrl: './classic-import-tree.component.html',
  styleUrl: './classic-import-tree.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClassicImportTreeComponent {
  readonly tree = input.required<ImportTreeNode[]>();
  /** Startauswahl; `null` heißt „alles“. */
  readonly selected = input<string[] | null>(null);
  readonly disabled = input(false);
  readonly selectionChange = output<string[] | null>();

  readonly types = computed(() => {
    const present = new Set(this.tree().map((n) => n.type));
    return TYPES.filter((t) => present.has(t));
  });
  readonly icons = ICONS;
  readonly rowHeight = ROW_HEIGHT_PX;

  readonly search = signal('');
  readonly type = signal<ImportElementType | null>(null);

  private readonly index = computed(() => indexTree(this.tree()));
  private readonly root = computed(() => buildFolderTree(this.tree()));

  private readonly manual = linkedSignal<Set<string>>(() => {
    const index = this.index();
    const selected = this.selected();
    return new Set(selected === null ? index.keys() : selected.filter((k) => index.has(k)));
  });

  private readonly expanded = linkedSignal<Set<string>>(() => {
    const root = this.root();
    return root.keys.length > COLLAPSE_ABOVE ? new Set() : new Set(allFolderKeys(root));
  });

  private readonly neededByKey = linkedSignal<Set<string>, string | null>({
    source: this.manual,
    computation: () => null,
  });

  readonly effective = computed(() => closure(this.manual(), this.index()));
  private readonly requirers = computed(() => requirers(this.manual(), this.index()));
  private readonly folderStates = computed(() => {
    const effective = this.effective();
    const states = new Map<string, FolderState>();
    const visit = (f: TreeFolder): void => {
      states.set(f.key, folderState(f.keys, effective));
      f.folders.forEach(visit);
    };
    this.root().folders.forEach(visit);
    return states;
  });

  readonly rows = computed(() => treeRows(this.root(), this.expanded(), { text: this.search(), type: this.type() }));
  readonly isEmpty = computed(() => this.tree().length === 0);
  readonly total = computed(() => this.effective().size);
  readonly autoCount = computed(() => this.effective().size - this.manual().size);

  readonly neededBy = computed<NeededBy | null>(() => {
    const key = this.neededByKey();
    if (key === null) return null;
    const index = this.index();
    const names = (this.requirers().get(key) ?? [])
      .map((k) => index.get(k)!.name)
      .sort((a, b) => a.localeCompare(b, 'de'));
    return { names: names.slice(0, MAX_NAMES), more: Math.max(0, names.length - MAX_NAMES) };
  });

  isExpanded(key: string): boolean {
    return this.expanded().has(key) || this.search().trim() !== '' || this.type() !== null;
  }

  isAuto(key: string): boolean {
    return this.effective().has(key) && !this.manual().has(key);
  }

  folderStateOf(key: string): FolderState {
    return this.folderStates().get(key) ?? 'none';
  }

  requiredByKey(key: string): { key: string; n: number } {
    const index = this.index();
    const list = this.requirers().get(key) ?? [];
    const cases = list.every((k) => index.get(k)!.type === 'TEST_CASE');
    const base = cases ? 'classicImport.tree.requiredByCases' : 'classicImport.tree.requiredByElements';
    return { key: list.length === 1 ? `${base}One` : base, n: list.length };
  }

  trackRow(_: number, row: TreeRow): string {
    return `${row.kind}:${row.key}`;
  }

  onSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value);
  }

  toggleFolderOpen(key: string): void {
    this.expanded.update((open) => {
      const next = new Set(open);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  }

  onToggle(row: TreeRow, event: MatCheckboxChange): void {
    const index = this.index();
    const keys = row.kind === 'folder' ? row.folder.keys : null;
    this.setManual(keys ? toggleKeys(this.manual(), keys, index) : toggleElement(this.manual(), row.key, index));
    // Der Klick schaltet die Checkbox selbst um; bleibt der Zustand gleich, kommt kein neuer Wert über die Bindung.
    if (keys) {
      const state = this.folderStateOf(row.key);
      event.source.checked = state === 'all';
      event.source.indeterminate = state === 'some';
    } else {
      event.source.checked = this.effective().has(row.key);
    }
  }

  selectAll(): void {
    this.setManual(new Set(this.index().keys()));
  }

  selectNone(): void {
    this.setManual(new Set());
  }

  showNeededBy(key: string): void {
    this.neededByKey.set(key);
  }

  closeNeededBy(): void {
    this.neededByKey.set(null);
  }

  private setManual(next: Set<string>): void {
    if (next === this.manual()) return;
    this.manual.set(next);
    this.selectionChange.emit(toSelection(next, this.index()));
  }
}

function allFolderKeys(f: TreeFolder): string[] {
  return f.folders.flatMap((sub) => [sub.key, ...allFolderKeys(sub)]);
}
